import React, { useRef, useState, useCallback, useMemo } from "react";

import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import { Box } from "@mui/material";
import { Form, Formik } from "formik";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import { updateUserPasswordById } from "../../Api/Services/UserServices";
import { PAWPRINT_USER_ROLE_KEY } from "../../Constant";
import { Body, ButtonBoxStyle, Column, FormHeaderBox } from "../../Css";
import { ResetPasswordValidation } from "../../From/Validation";
import CustomButton from "../Common/Button";
import FormGroup from "../Common/FormGroup";
import PasswordField from "../Common/PasswordField";
import { showSnackbar } from "../Common/Snackbar";
import { useTheme } from "@mui/material/styles";

const DEFAULT_VALUES = {
  oldPassword: "",
  newPassword: "",
  confirmPassword: "",
};

// User roles that are allowed to change password
const ALLOWED_USER_ROLES = [
  "User",
  "user", 
  "General Authorized User",
  "View Only User"
];

export default function UserEdit() {
  const user_id = localStorage.getItem("pawprint_user_id");
  const [initialValue, setInitialValue] = useState(DEFAULT_VALUES);
  const navigate = useNavigate();
  const formRef = useRef(null);
  const theme = useTheme();

  // Memoize user role check
  const userRole = useMemo(() => 
    localStorage.getItem(PAWPRINT_USER_ROLE_KEY), 
    []
  );

  const isUserAllowed = useMemo(() => 
    ALLOWED_USER_ROLES.includes(userRole), 
    [userRole]
  );

  const handleBackNavigation = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleSubmit = useCallback(async (values, { setSubmitting, setFieldError }) => {
    // Validate that new password is different from old password
    if (values.oldPassword === values.newPassword) {
      setFieldError(
        "newPassword",
        "New password cannot be the same as the Old password"
      );
      setSubmitting(false);
      return;
    }

    // Check if user has permission to change password
    if (!isUserAllowed) {
      setFieldError("oldPassword", "You don't have permission to change password");
      setSubmitting(false);
      return;
    }

    const formData = {
      oldPassword: values.oldPassword,
      newPassword: values.newPassword,
      confirmPassword: values.confirmPassword,
    };

    try {
      await updateUserPasswordById(user_id, formData);
      showSnackbar({
        type: "success",
        message: "Password updated successfully",
      });
    } catch (error) {
      console.error("Password update error:", error);

      const errorMessage = error.response?.data?.message || "Password change failed";
      setFieldError("oldPassword", errorMessage);
    } finally {
      setSubmitting(false);
    }
  }, [user_id, isUserAllowed]);

  return (
    <Container>
      <Box
        sx={{
          display: "flex",
          justifyContent: "left",
          alignItems: "center",
          gap: "10px",
        }}
      >
        <ArrowBackIosNewRoundedIcon
          sx={{ color: "black", cursor: "pointer" }}
          onClick={handleBackNavigation}
        />
        <FormTitle>Reset Your Password</FormTitle>
      </Box>
      <FormWrapper>
        <Formik
          initialValues={initialValue}
          innerRef={formRef}
          onSubmit={handleSubmit}
          validationSchema={ResetPasswordValidation}
          validateOnMount={false}
          enableReinitialize
        >
          {({ getFieldProps, isSubmitting }) => {
            // Helper function to create password field props
            const getPasswordFieldProps = (fieldName) => ({
              ...getFieldProps(fieldName),
              onCut: (e) => e.preventDefault(),
              onCopy: (e) => e.preventDefault(),
              onPaste: (e) => e.preventDefault(),
            });

            return (
              <Form>
                <Box sx={FormHeaderBox(theme.palette.primary.main)}>
                  <div className="scroll2" style={{ width: "100%" }}>
                    <Body>
                      <Column>
                        <FormGroup
                          name="oldPassword"
                          label="Enter your old password"
                          requiredField
                        >
                          <PasswordField {...getPasswordFieldProps("oldPassword")} />
                        </FormGroup>
                      </Column>
                    </Body>
                    <Body style={{ marginTop: "10px" }}>
                      <Column>
                        <FormGroup
                          name="newPassword"
                          label="Enter your new password"
                          requiredField
                        >
                          <PasswordField {...getPasswordFieldProps("newPassword")} />
                        </FormGroup>
                      </Column>
                    </Body>
                    <Body style={{ marginTop: "10px" }}>
                      <Column>
                        <FormGroup
                          name="confirmPassword"
                          label="Confirm your password"
                          requiredField
                        >
                          <PasswordField {...getPasswordFieldProps("confirmPassword")} />
                        </FormGroup>
                      </Column>
                    </Body>
                  </div>
                  <Box sx={ButtonBoxStyle(`${theme.palette.primary.main}`)}>
                    <CustomButton
                      type="submit"
                      disabled={isSubmitting}
                      label={isSubmitting ? "Saving..." : "Save"}
                    />
                  </Box>
                </Box>
              </Form>
            );
          }}
        </Formik>
      </FormWrapper>
    </Container>
  );
}

const Container = styled.div`
  padding: 20px;
  @media (max-width: 450px) {
    padding: 10px;
  }
`;

const FormTitle = styled.h2`
  text-align: center;
  font-size: 20px;
  font-weight: 500;
  font-style: normal;
  color: #000000;
`;

const FormWrapper = styled.div`
  justify-content: center;
  align-items: center;
  // margin: 0 auto;
  // width: 90%;
`;
